/**
 * Public-facing Exercise Library — lets users browse the 40+ canonical
 * movements, search by name / muscle group, and tap any card to open the
 * read-only demo modal (video / form tips / equipment / muscles).
 *
 * Videos & thumbnails come from admin uploads — until the admin fills them
 * in, each entry gracefully shows a "Demo video coming soon" placeholder.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Search, Dumbbell, Info, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ExerciseDemoModal } from '../components/workout-detail/ExerciseDemoModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MUSCLE_FILTERS = [
  'all', 'chest', 'back', 'shoulders', 'biceps', 'triceps', 'core',
  'quads', 'hamstrings', 'glutes', 'calves', 'full body',
];

export const ExerciseLibraryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMuscle = searchParams.get('muscle') || 'all';
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [muscle, setMuscle] = useState(MUSCLE_FILTERS.includes(initialMuscle) ? initialMuscle : 'all');
  const [active, setActive] = useState(null);

  // Keep ?muscle= in sync with the dropdown so deep-links share cleanly.
  const selectMuscle = (m) => {
    setMuscle(m);
    if (m === 'all') {
      searchParams.delete('muscle');
    } else {
      searchParams.set('muscle', m);
    }
    setSearchParams(searchParams, { replace: true });
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND}/api/exercises`, { withCredentials: true })
      .then((res) => { if (alive) setExercises(res.data?.exercises || []); })
      .catch(() => { if (alive) setExercises([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    let list = exercises;
    if (muscle !== 'all') {
      list = list.filter((e) => (e.muscle_groups || []).includes(muscle));
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((e) =>
        e.name.toLowerCase().includes(q) ||
        (e.aliases || []).some((a) => a.toLowerCase().includes(q)) ||
        (e.muscle_groups || []).some((m) => m.toLowerCase().includes(q))
      );
    }
    return list;
  }, [exercises, query, muscle]);

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="exercise-library-page">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <header className="mb-6">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-wider">
            Exercise <span className="text-primary">Library</span>
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-2 max-w-2xl">
            Tap any exercise to see muscle groups, equipment, and form tips. Demo
            videos for every move are being uploaded — you'll see "coming soon"
            placeholders until they're live.
          </p>
        </header>

        {/* Coming-soon notice */}
        <div className="mb-5 p-3 rounded-xl border border-primary/30 bg-primary/5 text-sm text-primary/90 flex items-start gap-2" data-testid="exercise-library-coming-soon">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong>Demo videos coming soon!</strong> We're filming the entire library — for now you'll see muscle-group tags, equipment, and form tips for every exercise.
          </span>
        </div>

        {/* Search + muscle filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, alias, or muscle…"
              className="pl-10 bg-card border-border text-white h-12"
              data-testid="exercise-library-search"
            />
          </div>
          <div className="sm:w-56" data-testid="exercise-library-muscle-filter">
            <Select value={muscle} onValueChange={selectMuscle}>
              <SelectTrigger
                data-testid="exercise-library-muscle-select"
                className="h-12 bg-card border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-4 hover:border-primary hover:bg-card/80 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
              >
                <SelectValue placeholder="All Muscle Groups" />
              </SelectTrigger>
              <SelectContent
                className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20 max-h-72"
                position="popper"
              >
                {MUSCLE_FILTERS.map((m) => (
                  <SelectItem
                    key={m}
                    value={m}
                    data-testid={`exercise-library-filter-${m.replace(/\s/g, '-')}`}
                    className="font-heading uppercase tracking-wider text-sm cursor-pointer text-white hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 [&>span:first-child]:text-primary"
                  >
                    {m === 'all' ? 'All Muscle Groups' : m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Count chip */}
        <div className="text-muted-foreground text-sm mb-3">
          Showing <span className="text-white font-medium">{filtered.length}</span> of {exercises.length} exercises
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-2xl">
            <Dumbbell className="h-8 w-8 mx-auto mb-3 opacity-50" />
            No exercises match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="exercise-library-grid">
            {filtered.map((ex) => (
              <button
                key={ex.id}
                onClick={() => setActive(ex)}
                className="text-left bg-card border border-border hover:border-primary p-4 rounded-2xl transition-colors group card-hover"
                data-testid={`exercise-library-card-${ex.slug}`}
              >
                <div className="flex items-start gap-3">
                  {(ex.thumbnail_url || ex.image_url) ? (
                    <img
                      src={ex.thumbnail_url || ex.image_url}
                      alt=""
                      className="h-16 w-16 rounded-lg object-cover border border-border shrink-0"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-black/40 border border-border flex items-center justify-center shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
                      <Dumbbell className="h-6 w-6" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base text-white uppercase tracking-wider truncate group-hover:text-primary transition-colors">
                      {ex.name}
                    </h3>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {(ex.muscle_groups || []).slice(0, 3).map((m) => (
                        <Badge key={m} className="text-[10px] bg-primary/20 text-primary border-0 capitalize">
                          {m}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider mt-2">
                      {ex.equipment || 'bodyweight'} · {(ex.video_url || ex.youtube_url) ? 'Video ✓' : 'Demo soon'}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {active && (
        <ExerciseDemoModal exercise={active} onClose={() => setActive(null)} />
      )}
    </div>
  );
};

export default ExerciseLibraryPage;
